import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { FocusShell } from "@/components/layout/FocusShell";
import { Button, ButtonLink, Notice, Panel, Status } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import {
  acceptingLabel,
  formatAvailabilityDate,
  waitBucketLabel,
  type Availability,
  type PracticeGroupSummary,
} from "@/lib/availability";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { SEO } from "@/lib/seo";

interface ConfirmFacility {
  id: string;
  name: string;
  availability: Availability | null;
}

interface ConfirmPeekResponse {
  kind: "confirm";
  group: PracticeGroupSummary | null;
  facilities: ConfirmFacility[];
  expiresAt: string;
}

type ConfirmResponse =
  | { ok: true; confirmed: number }
  | { ok: true; claimUrl: string };

export default function ConfirmLink() {
  const { token = "" } = useParams<{ token: string }>();
  const { city } = useCity();
  const [confirmedCount, setConfirmedCount] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const confirmQuery = useQuery({
    queryKey: ["confirm-link", city.slug, token],
    queryFn: () =>
      api.get<ConfirmPeekResponse>(`/v1/public/${city.slug}/confirm/${token}`, {
        skipAuth: true,
      }),
    enabled: Boolean(token),
    retry: false,
  });

  const respond = useMutation({
    mutationFn: (stillAccurate: boolean) =>
      api.post<ConfirmResponse>(
        `/v1/public/${city.slug}/confirm/${token}`,
        { stillAccurate },
        { skipAuth: true },
      ),
    onSuccess: (response, stillAccurate) => {
      if (stillAccurate && "confirmed" in response) {
        track("confirm_yes", { facility_count: response.confirmed });
        setConfirmedCount(response.confirmed);
        return;
      }

      if (!stillAccurate && "claimUrl" in response) {
        track("confirm_changed", {
          facility_count: confirmQuery.data?.facilities.length ?? 0,
        });
        window.location.assign(response.claimUrl);
      }
    },
    onError: (error) => {
      setActionError(
        error instanceof Error && error.message
          ? error.message
          : "We could not record that response. Please try again.",
      );
    },
  });

  const groupName =
    confirmQuery.data?.group?.name ??
    confirmQuery.data?.facilities[0]?.name ??
    "Referral availability";

  return (
    <FocusShell>
      <SEO
        title={`Confirm referral availability · ${groupName}`}
        description="Confirm that current specialist referral availability is still accurate."
        path="/confirm"
        noindex
      />
      {confirmQuery.isLoading ? (
        <Panel><p className="ds-small" role="status">Checking this confirmation link…</p></Panel>
      ) : confirmQuery.isError || !confirmQuery.data ? (
        <Panel>
          <h1 className="ds-display-2">This link is no longer available</h1>
          <p className="ds-body">
            It may have expired or already been used. Opening it did not change any availability. Email
            hello@pulseboard.com.au for a new link.
          </p>
        </Panel>
      ) : confirmedCount !== null ? (
        <Panel>
          <span className="ds-empty__icon" aria-hidden="true"><CheckCircle2 /></span>
          <div className="ds-stack" style={{ gap: 8 }}>
            <p className="ds-eyebrow ds-eyebrow--teal">Confirmation saved</p>
            <h1 className="ds-display-2">Thanks, availability is current</h1>
            <p className="ds-body">
              {confirmedCount} {confirmedCount === 1 ? "doctor now shows" : "doctors now show"} a fresh confirmation date.
              We'll check again in four weeks.
            </p>
          </div>
          <div><ButtonLink to="/directory" variant="secondary">View the directory</ButtonLink></div>
        </Panel>
      ) : (
        <Panel>
          <div className="ds-stack" style={{ gap: 8 }}>
            <p className="ds-eyebrow ds-eyebrow--teal">Four-week check</p>
            <h1 className="ds-display-2">Is this still accurate for {groupName}?</h1>
            <p className="ds-small">Review the current status, then choose one answer for all doctors below.</p>
          </div>
          <dl className="ds-dl ds-dl--rows">
            {confirmQuery.data.facilities.map((facility) => (
              <div key={facility.id}>
                <dt className="ds-stack" style={{ gap: 2 }}>
                  <span style={{ color: "var(--ds-ink)", fontWeight: 500 }}>{facility.name}</span>
                  <span className="ds-fine">
                    {facility.availability
                      ? waitBucketLabel(facility.availability.waitBucket) ?? "Wait not published"
                      : "Availability not published"}
                  </span>
                </dt>
                <dd className="ds-stack" style={{ gap: 4, justifyItems: "end" }}>
                  <Status tone={facility.availability ? (facility.availability.accepting === "yes" ? "ok" : facility.availability.accepting === "urgent_only" ? "urgent" : "warn") : "neutral"}>
                    {facility.availability ? acceptingLabel(facility.availability.accepting) : "Not confirmed"}
                  </Status>
                  <span className="ds-fine">
                    {facility.availability ? `Confirmed ${formatAvailabilityDate(facility.availability.confirmedAt)}` : "No confirmation date"}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
          {actionError ? <Notice tone="error">{actionError}</Notice> : null}
          <div className="ds-cluster">
            <Button
              disabled={respond.isPending}
              onClick={() => {
                setActionError(null);
                respond.mutate(true);
              }}
            >
              {respond.isPending ? "Saving…" : "Yes, still accurate"}
            </Button>
            <Button
              variant="secondary"
              disabled={respond.isPending}
              onClick={() => {
                setActionError(null);
                respond.mutate(false);
              }}
            >
              Something changed
            </Button>
          </div>
        </Panel>
      )}
    </FocusShell>
  );
}
